import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ButtonV2,
  LabraToolTipV2,
  SidePanelInfo,
  TableComponentV2,
  StatusChipV4,
} from '@labrav/react-components'
import { DateTime } from 'luxon'
import { Box, Typography } from '@mui/material'
import {
  isLoading,
  LoadingTypes,
  searchDropDownOptionMapping,
  ShortenedNameV2,
  standardLabraDateFormat,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
} from '@labrav/common'

import { useDispatch, useSelector } from '../../../store/hooks'
import { helpDrawerUrl } from '../../../common/utils/revenueConstants'
import { getDateDiff, getFormattedLabraDate } from '../../../common/utils/dates'
import { getStatusAndLabelForRevenueInsights } from '../../../common/utils/getStatusAndLabelForRevenueInsights'
import { useStyles } from './RevenueInsightsTableView.styles'
import placeholder from '../../../common/images/placeholder.svg'
import { getRevenueInsights } from '../../../modules/revenueInsightsV2/action'
import {
  DisbursementStatus,
  LastEvaluatedObject,
  OfferVisibilitySatus,
  ReportsRow,
} from '../../../modules/revenueInsightsV2/types'
import { RevenueSidePanelWrapper } from '../RevenueInsightsSidePanel/RevenueSidePanelWrapper'
import { formatToDecimalPlaces } from '../../../common/utils/revenueHelpers'
import { getCurrencySymbol } from '../../../common/utils/getCurrencySymbol'

export const RevenueInsightsTableView: React.FC<{
  reportsRows: ReportsRow[]
  isDataFeedsConnected?: boolean
  input?: string
  selectedOption?: string
}> = ({ isDataFeedsConnected, reportsRows, input, selectedOption }) => {
  const dispatch = useDispatch()
  const { accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const { classes } = useStyles({ isDataFeedsConnected })
  const [selectedId, setSelectedId] = useState<string>('')
  const { pageSize: pageSizeValue } = useScreenSize()
  const [selectedRow, setSelectedRow] = useState<ReportsRow>({} as ReportsRow)
  const navigate = useNavigate()
  const { selectedProduct: productId } = useFlyOutProductsBasedOnSelectedCloud()
  const pageNumber = useSelector<number | undefined>(
    state => state.revenueInsightsV2.pageNumber
  )
  const count = useSelector<number | undefined>(
    state => state.revenueInsightsV2.count
  )
  const lastEvaluatedObject = useSelector<LastEvaluatedObject | null>(
    state => state.revenueInsightsV2?.lastEvaluatedObject || null
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const offerVisibilitySatusText = {
    [OfferVisibilitySatus.PRIVATE]: 'Private',
    [OfferVisibilitySatus.PUBLIC]: 'Public',
    [OfferVisibilitySatus.CPPO]: 'Channel',
  }

  const onChangeRow = (selectedId: string) => {
    setSelectedId(selectedId)
    const reportRow = reportsRows.filter(row => {
      const idField =
        selectedCloudTypeProductListing === 'AZURE'
          ? row.itemId
          : selectedCloudTypeProductListing === 'GCP'
            ? row.externalRecordId
            : row.invoiceId
      return idField === selectedId
    })[0]

    const dateKeysToBeConverted: (keyof ReportsRow)[] = [
      'disbursementDate',
      'invoiceDate',
      'paymentDueDate',
      'agreementStartDate',
      'agreementAcceptanceDate',
      'agreementEndDate',
      'lastCollectionDate',
    ]
    const reportDataClone = { ...reportRow }
    dateKeysToBeConverted.forEach(key => {
      if (reportDataClone[key] === 'NA' || reportDataClone[key] === null) {
        ;(reportDataClone[key] as string) = 'NA'
      } else {
        ;(reportDataClone[key] as string) = DateTime.fromISO(
          reportDataClone[key] as string
        ).toFormat(standardLabraDateFormat)
      }
    })
    setSelectedRow(reportDataClone)
  }

  const fetchMoreData = useCallback(() => {
    dispatch(
      getRevenueInsights({
        downloadReport: false,
        partnerId: accountId,
        pageSizeValue,
        input,
        selectedInput:
          searchDropDownOptionMapping.revenueInsightsOptions[
            selectedOption as keyof typeof searchDropDownOptionMapping.revenueInsightsOptions
          ],
        sectionName: 'revenue-feeds',
        pageNumber: (pageNumber as number) + 1,
        lastEvaluatedObject,
        cloudMarketplace: selectedCloudTypeProductListing,
      })
    )
  }, [
    productId,
    lastEvaluatedObject,
    pageNumber,
    pageSizeValue,
    input,
    selectedCloudTypeProductListing,
  ])

  const hasMoreData = useMemo(
    () => lastEvaluatedObject != null,
    [lastEvaluatedObject]
  )

  const onCloseSidePanel = () => {
    setSelectedId('')
  }
  const isOpen = !!selectedId

  const awsRevenueInsightsMapping = {
    Company: 'buyerCompanyName',
    ...(!productId ? { 'Product title': 'productTitle' } : {}),
    'Net revenue': 'sellerNetRevenue',
    'Payment due date': 'paymentDueDate',
    'Disbursement status': 'disbursementStatus',
    'Disbursement date': 'disbursementDate',
    'Invoice date': 'invoiceDate',
    'Payment terms': 'paymentTerms',
    'Offer type': 'offerVisibility',
    'Offer ID': 'awsOfferId',
    'Account ID': 'buyerAwsAccountId',
  }

  const azureRevenueInsightsMapping = {
    Company: 'buyerCompanyName',
    ...(!productId ? { 'Product title': 'productTitle' } : {}),
    'Net revenue': 'sellerNetRevenue',
    'Payment due date': 'paymentDueDate',
    'Disbursement status': 'disbursementStatus',
    'Disbursement date': 'disbursementDate',
    'Invoice date': 'invoiceDate',
    'Offer type': 'offerVisibility',
    'Offer ID': 'cloudOfferId',
    'Account ID': 'cloudBuyerAccountId',
  }

  const gcpRevenueInsightsMapping = {
    Buyer: 'buyerCompanyName',
    ...(!productId ? { Product: 'productTitle' } : {}),
    'Net revenue': 'sellerNetRevenue',
    'Disbursement status': 'disbursementStatus',
    'Disbursement date': 'disbursementDate',
    'Offer type': 'offerVisibility',
    'Offer ID': 'offerId',
    'Account ID': 'accountId',
  }

  const revenueInsightsMapping = useMemo(() => {
    if (selectedCloudTypeProductListing === 'AWS') {
      return awsRevenueInsightsMapping
    } else if (selectedCloudTypeProductListing === 'AZURE') {
      return azureRevenueInsightsMapping
    } else if (selectedCloudTypeProductListing === 'GCP') {
      return gcpRevenueInsightsMapping
    }
    return {}
  }, [selectedCloudTypeProductListing, productId])

  const processPaymentDueDate = (paymentDueDate: string) => {
    const { dayDiff } = getDateDiff(paymentDueDate)
    const isPaymentDueDateExpired =
      DateTime.fromISO(paymentDueDate).endOf('day') <
      DateTime.now().endOf('day')
    const { formattedDate: formattedPaymentDueDate } =
      getFormattedLabraDate(paymentDueDate)
    return {
      dayDiff,
      isPaymentDueDateExpired,
      formattedPaymentDueDate,
    }
  }
  const revenueInsightObject =
    (reportsRows || []).length > 0
      ? reportsRows.map(reportData => {
          const report = { ...reportData } as ReportsRow
          const { formattedDate: invoiceDate } = getFormattedLabraDate(
            report.invoiceDate as string
          )
          const currencySymbol = getCurrencySymbol(report.currency as string)
          const { dayDiff, isPaymentDueDateExpired, formattedPaymentDueDate } =
            processPaymentDueDate(report.paymentDueDate as string)
          const statusLabel = getStatusAndLabelForRevenueInsights(
            report?.disbursementStatus as DisbursementStatus
          )

          const offerTypeValue = report?.offerType || report?.offerVisibility
          return {
            id:
              selectedCloudTypeProductListing === 'AZURE'
                ? (report.itemId as string)
                : selectedCloudTypeProductListing === 'GCP'
                  ? (report.externalRecordId as string)
                  : report.invoiceId,
            buyerCompanyName: (
              <div className={classes.buyerName}>
                <img
                  src={
                    report.labraBuyerLogoUrl
                      ? report.labraBuyerLogoUrl
                      : placeholder
                  }
                  data-testid="company-logo"
                  className={classes.image}
                />

                <Typography className={classes.companyName}>
                  <ShortenedNameV2
                    name={
                      ((report?.buyerCompanyName
                        ? report?.buyerCompanyName
                        : report?.buyerEmailDomain) as string) ||
                      report?.companyName ||
                      ''
                    }
                    length={15}
                  />
                </Typography>
              </div>
            ),
            productTitle: (
              <ShortenedNameV2 name={report?.productTitle} length={30} />
            ),
            sellerNetRevenue: (
              <LabraToolTipV2
                description={
                  <div
                    className={classes.toolTipWrapper}
                    data-testid="toolTip-netaRevenue"
                  >
                    <div className={classes.grossRevenue}>
                      <Typography className={classes.toolTipText}>
                        Gross revenue:
                      </Typography>
                      <Typography className={classes.toolTipText}>
                        {currencySymbol}
                        {formatToDecimalPlaces(
                          report?.grossAmount || report?.grossRevenue
                        )}
                      </Typography>
                    </div>
                    <div className={classes.listingWrapper}>
                      <Typography className={classes.toolTipText}>
                        Listing fee:
                      </Typography>
                      <Typography className={classes.toolTipText}>
                        {currencySymbol}
                        {formatToDecimalPlaces(report?.listingFee)}
                      </Typography>
                    </div>
                    <div className={classes.listingWrapper}>
                      <Typography className={classes.toolTipText}>
                        Listing %:
                      </Typography>
                      <Typography className={classes.toolTipText}>
                        {formatToDecimalPlaces(report?.listingFeePercentage)}
                      </Typography>
                    </div>
                  </div>
                }
                tooltipPlacement="bottom"
              >
                {currencySymbol}
                {formatToDecimalPlaces(
                  report?.sellerNetRevenue || report?.netRevenue
                )}
              </LabraToolTipV2>
            ),
            paymentDueDate: report.paymentDueDate ? (
              isPaymentDueDateExpired &&
              report.disbursementStatus === DisbursementStatus.NOT_DISBURSED ? (
                <LabraToolTipV2
                  description={`Payment delayed by ${Math.abs(dayDiff)} days.`}
                  tooltipPlacement="bottom"
                >
                  <Typography className={classes.paymentDueDate}>
                    {formattedPaymentDueDate}
                  </Typography>
                </LabraToolTipV2>
              ) : (
                formattedPaymentDueDate
              )
            ) : (
              '-'
            ),
            disbursementStatus: report?.disbursementStatus ? (
              <StatusChipV4 {...statusLabel} />
            ) : (
              '-'
            ),
            disbursementDate: report?.disbursementDate
              ? report?.disbursementDate === 'NA'
                ? 'NA'
                : DateTime.fromISO(report?.disbursementDate as string).toFormat(
                    standardLabraDateFormat
                  )
              : '-',
            invoiceDate: report?.invoiceDate ? invoiceDate : '-',
            paymentTerms: `Net ${report?.paymentTerms as string}`,
            offerVisibility:
              offerTypeValue && offerTypeValue in offerVisibilitySatusText
                ? offerVisibilitySatusText[
                    offerTypeValue as keyof typeof offerVisibilitySatusText
                  ]
                : (offerTypeValue as string),
            awsOfferId: report?.awsOfferId as string,
            buyerAwsAccountId: report?.buyerAwsAccountId as string,
            cloudOfferId: report?.cloudOfferId as string,
            cloudBuyerAccountId: report?.cloudBuyerAccountId as string,
            offerId: report?.offerId as string,
            accountId: report?.accountId as string,
          }
        })
      : []

  const redirectToDataFeeds = () => {
    navigate(`/settings/flyout/cloud/aws/${accountId}`, {
      state: { scrollToSetup: true },
    })
  }
  const DataFeedsRedirection = (
    <Box
      className={classes.responseStyle}
      data-testid="redirect-data-feeds-wrapper"
    >
      <div className={classes.dataFeedsWrapper}>
        <Typography className={classes.responseText}>
          Click the below button to setup data feeds to start seeing your
          revenue insights.
        </Typography>
        <ButtonV2 data-testid="set-up-data-feeds" onClick={redirectToDataFeeds}>
          Set up data feeds
        </ButtonV2>
      </div>
    </Box>
  )

  const openHelpUrl = () => {
    const newWindow = window.open(
      helpDrawerUrl,
      '_blank',
      'noopener, noreferrer'
    )
    if (newWindow) newWindow.opener = null
  }

  const hasMoreBoolean =
    selectedCloudTypeProductListing === 'AWS' && !input ? true : false

  return (
    <>
      <div data-testid="revenue-tableV2" className={classes.table}>
        <TableComponentV2
          keyName={'RevenueInsights'}
          objects={revenueInsightObject}
          mapping={revenueInsightsMapping}
          fetchMoreData={fetchMoreData}
          selectedItem={[]}
          onChangeCheckbox={() => void 0}
          showCheckbox={false}
          isLoading={loading}
          onChangeRow={onChangeRow}
          breakWorkHeader={false}
          tableHeight={
            isDataFeedsConnected ? 'calc(100vh - 290px)' : 'fit-content'
          }
          {...(hasMoreBoolean
            ? { hasMoreData: hasMoreData }
            : { count: count })}
          isChildrenShow={!isDataFeedsConnected}
          children={DataFeedsRedirection}
          rightAlignColumns={['sellerNetRevenue']}
        />
      </div>
      <SidePanelInfo
        icon={
          <img
            src={
              selectedRow?.labraBuyerLogoUrl
                ? selectedRow?.labraBuyerLogoUrl
                : placeholder
            }
            data-testid="company-logo"
            className={classes.image}
          />
        }
        title=""
        headerTitle={
          <div className={classes.headerText}>
            {selectedRow?.buyerCompanyName || selectedRow?.companyName || '-'}
          </div>
        }
        open={isOpen}
        onClose={onCloseSidePanel}
        content={<RevenueSidePanelWrapper reportData={selectedRow} />}
        getHelp={openHelpUrl}
      />
    </>
  )
}
